import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { KitFontId, RELEASE_THEMES, ReleaseThemeId, themeColours } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { ThemeStill } from '../../themes/ThemeStill';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { kitFontName, kitFontStyle } from '../../themes/kitWebFonts';
import { ThemeStillSong } from './useThemeStillSong';
import { themeMessages } from './themeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Theme panel; each part uses its own slice of it */
import styles from './ThemePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ThemeSummaryProps {
  themeId: ReleaseThemeId;
  fontOverride: KitFontId | null;
  palette: readonly string[];
  song: ThemeStillSong;
  /** The id of the full panel body that "Edit theme" opens (`aria-controls`). */
  bodyId: string;
  onEdit: () => void;
}

/**
 * The closed Theme panel of the song page: what is picked, in one line. A small still of the song
 * in the theme, the theme name and its feeling, the title font, the 4 theme colours, and "Edit
 * theme", which opens the full panel (the 8 tiles, the preview, the font, the logo, the default).
 */
const ThemeSummaryComponent: React.FC<ThemeSummaryProps> = ({
  themeId,
  fontOverride,
  palette,
  song,
  bodyId,
  onEdit,
}) => {
  const intl = useIntl();
  const colours = useMemo(() => themeColours(themeId, palette), [palette, themeId]);
  const font = fontOverride ?? RELEASE_THEMES[themeId].type.display;
  const messages = releaseThemeMessages[themeId];

  return (
    <div className={styles.summary} data-testid="theme-summary">
      <span className={styles.summaryTile}>
        <ThemeStill
          themeId={themeId}
          fontOverride={fontOverride}
          palette={palette}
          artworkUrl={song.artworkUrl}
          title={song.title.slice(0, 1)}
          artist=""
          kicker=""
          mini
          testId="theme-summary-still"
        />
      </span>
      <div className={styles.summaryText}>
        <b className={styles.summaryName}>{intl.formatMessage(messages.name)}</b>
        <span className={styles.summaryLine}>{intl.formatMessage(messages.line)}</span>
        <span className={styles.summaryMeta}>
          <span style={kitFontStyle(font)}>
            {intl.formatMessage(themeMessages.fontTitle, { font: kitFontName(font) })}
          </span>
          <span className={styles.summaryDots} data-testid="theme-summary-colours" aria-hidden="true">
            {[colours.background, colours.surface, colours.text, colours.accent].map((colour, index) => (
              // The 4 slots can share a colour, so the slot index keeps the key unique.
              // eslint-disable-next-line react/no-array-index-key
              <i key={index} className={styles.summaryDot} style={{ background: colour }} />
            ))}
          </span>
        </span>
      </div>
      <SMLButton onClick={onEdit} ariaExpanded={false} ariaControls={bodyId}>
        {intl.formatMessage(themeMessages.edit)}
      </SMLButton>
    </div>
  );
};

export const ThemeSummary = TypedMemo(ThemeSummaryComponent);
